/**
 * 귀환 목적지 팝업 (UNI-Q22) — 서버 `returnOptions` 가 후보를 둘 이상 줄 때만 뜬다.
 * 후보가 하나면 서버가 바로 시전하므로 이 파일은 안 불린다(io 기준선).
 * 고르면 `{t:'scroll', dest, remember}` 를 보내고 서버가 다시 검증한다. 좌표는 서버만 안다.
 * 키보드: 1·2·3 으로 고르고 Esc 로 닫는다. 터치: 44px 버튼. 390px 에서 한 열.
 */
import { send } from '../core/net.js';

let root = null;
let options = [];

function close() {
  if (!root) return;
  root.remove(); root = null; options = [];
  window.removeEventListener('keydown', onKey, true);
}

function choose(dest) {
  if (!options.some((c) => c.id === dest)) return;
  const remember = !!root?.querySelector('#return-remember')?.checked;
  close();
  // F28 — 고른 목적지를 이동 막 문장(「집으로 이동하는 중…」)이 쓴다(render/teleport-fx.js 가 듣는다 — 그림 모듈을 가져오지 않는다)
  globalThis.dispatchEvent?.(new CustomEvent('unige:recall-dest', { detail: dest }));
  send({ t: 'scroll', dest, remember });
}

function onKey(e) {
  if (!root) return;
  if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); return; }
  const n = Number(e.key);
  // R141 — 스킬 키(1~6)를 누른 채였으면 그 자동 반복이 목적지를 골라 버린다. 새로 누른 키만 고른다.
  if (Number.isInteger(n) && n >= 1 && n <= options.length) { e.preventDefault(); e.stopPropagation(); if (!e.repeat) choose(options[n - 1].id); return; }
  // 팝업이 떠 있는 동안 다른 단축키(R 재시전·i 가방 등)는 삼킨다 — 두 겹으로 열리지 않게.
  if (!e.ctrlKey && !e.metaKey && !e.altKey && /^[a-z]$/i.test(e.key)) { e.stopPropagation(); }
}

const escHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
// 겹친 템플릿 리터럴을 피한다(check-dead-state 의 문자열 걷기가 겹침에서 어긋난다) — 행 하나는 함수 하나.
const optionButton = (c, i) => '<button type="button" data-dest="' + escHtml(c.id) + '"><i>' + (i + 1) + '</i><span>' + escHtml(c.label) + '<small>' + escHtml(c.detail || '') + '</small></span></button>';

/** 후보 목록으로 팝업을 그린다. 이미 떠 있으면 새 목록으로 갈아 끼운다. */
export function showReturnOptions(candidates) {
  const list = (Array.isArray(candidates) ? candidates : []).filter((c) => c && typeof c.id === 'string');
  if (list.length < 2) { close(); return; }
  close();
  options = list;
  const box = document.createElement('div');
  box.id = 'return-pick';
  box.className = 'on';
  box.setAttribute('role', 'dialog');
  box.setAttribute('aria-modal', 'true');
  box.setAttribute('aria-label', '귀환 목적지');
  box.innerHTML = `<div class="box">
    <b>어디로 귀환할까?</b>
    <p>장소를 누르면 귀환을 시작한다. 숫자 키로도 고를 수 있다.</p>
    <div class="opts">${list.map(optionButton).join('')}</div>
    <label><input type="checkbox" id="return-remember"><span>다음에도 여기로 귀환 · 부동산에서 변경</span></label>
    <button type="button" class="ghost cancel">취소 (Esc)</button>
  </div>`;
  box.addEventListener('pointerdown', (e) => e.stopPropagation());
  box.addEventListener('click', (e) => { e.stopPropagation(); if (e.target === box) close(); });
  box.querySelectorAll('.opts button').forEach((b) => { b.onclick = () => choose(b.dataset.dest); });
  box.querySelector('.cancel').onclick = close;
  (document.getElementById('game') || document.body).append(box);
  root = box;
  window.addEventListener('keydown', onKey, true);
  box.querySelector('.opts button')?.focus({ preventScroll: true });
}

export function hideReturnOptions() { close(); }

// 나가면 닫는다 — 선택 화면 위에 귀환 팝업이 떠 있을 이유가 없다. 사망은 main.js 의 died 처리가 hideReturnOptions 를 부른다.
addEventListener('unige:logout', close);
